import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {gridColumnFieldsSelector, useGridApiRef} from '@mui/x-data-grid-pro';
import {assert} from 'assert-ts';
import type {ViewMode} from 'services/advancedSearch/types';
import {useDataGridSettings} from 'services/settings/hooks';
import {useDataTablePagination} from 'components/dataTable/hooks';
import type {AdvancedSearchContextType} from '../types';
import {advancedSearchSchemaName} from '../types';
import {reorderColumns} from '../functions/reorderColumns';
import {
  useAvailableFiltersWithLabel,
  useCurrentColumns,
  useCurrentFilters,
  useDataFetch,
  useGetMetadataIds,
  useSort,
  useViewMode,
} from '../hooks';
import {mapDataToMetadataId} from '../mappers';

export const AdvancedSearchContext = createContext<
  AdvancedSearchContextType | undefined
>(undefined);

export const useAdvancedSearchContextProviderValue =
  (): AdvancedSearchContextType => {
    const {statusAvailableFilters, availableFilters} =
      useAvailableFiltersWithLabel();

    const {
      currentFilters,
      addCurrentFilter,
      setCurrentFilters,
      updateCurrentFilter,
      removeCurrentFilterById,
    } = useCurrentFilters();

    // Grid state is only read on mount and saved on unload; columns, sorting
    // and pagination are mirrored locally to drive the search.
    const tableApiRef = useGridApiRef();
    const {initialState: tableInitialState, saveState: onTableSaveState} =
      useDataGridSettings(advancedSearchSchemaName, {
        storeSize: true,
        storePage: true,
        storeSorting: true,
      });

    const {
      loadingColumns,
      allColumns,
      currentColumns,
      schema,
      setCurrentColumns,
      addCurrentColumn,
      removeCurrentColumn,
      updateCurrentColumns,
    } = useCurrentColumns(tableInitialState?.columns);

    const {
      paginationModel,
      onPaginationModelChange: onTablePaginationModelChange,
      resetPagination,
    } = useDataTablePagination(
      tableApiRef,
      tableInitialState?.pagination?.paginationModel,
    );

    const {onSearch, onDownload, dataResponse, loadingSearch, loadingDownload} =
      useDataFetch(allColumns);

    const {viewMode, setViewMode} = useViewMode({updateCurrentColumns});

    const getMetadataIds = useGetMetadataIds();

    const {sort, onSortModelChange: onTableSortModelChange} = useSort(
      tableInitialState?.sorting?.sortModel,
    );

    // The grid owns the column order, so read it when columns are used
    const orderByGrid = useCallback(
      (columns: string[]) =>
        tableApiRef.current
          ? reorderColumns(columns, gridColumnFieldsSelector(tableApiRef))
          : columns,
      [tableApiRef],
    );

    const loading = useMemo(() => {
      return (
        loadingColumns || [statusAvailableFilters].some(s => s === 'Loading')
      );
    }, [loadingColumns, statusAvailableFilters]);

    const handleOnDownload = useCallback(
      (
        viewMode: ViewMode,
        columns: string[] | undefined,
        distinctOn?: string,
      ) =>
        onDownload(
          viewMode,
          currentFilters,
          columns ? orderByGrid(columns) : columns,
          distinctOn,
        ),
      [currentFilters, onDownload, orderByGrid],
    );

    const handleOnSearch = useCallback(
      (resetPage?: boolean) => {
        if (resetPage) {
          resetPagination();
        }

        onSearch({
          type: viewMode,
          filters: currentFilters,
          columns: orderByGrid(currentColumns),
          sort,
          page: resetPage ? 0 : paginationModel.page,
          size: paginationModel.pageSize,
        });
      },
      [
        onSearch,
        viewMode,
        currentFilters,
        currentColumns,
        sort,
        paginationModel.page,
        paginationModel.pageSize,
        resetPagination,
        orderByGrid,
      ],
    );

    const handleGetMetadataIdsPage = useCallback(
      (page: number, size: number) => {
        return getMetadataIds(viewMode, currentFilters, sort, page, size);
      },
      [getMetadataIds, viewMode, currentFilters, sort],
    );

    const handleGetCurrentMetadataIds = useCallback(() => {
      return dataResponse.rows.map(mapDataToMetadataId);
    }, [dataResponse]);

    useEffect(() => {
      onSearch({
        type: viewMode,
        filters: currentFilters,
        columns: orderByGrid(currentColumns),
        sort,
        page: paginationModel.page,
        size: paginationModel.pageSize,
      });
      // Don't search on every filter change
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      viewMode,
      currentColumns,
      sort,
      paginationModel.page,
      paginationModel.pageSize,
    ]);

    return useMemo(
      () => ({
        loading,
        availableFilters,
        currentFilters,
        addCurrentFilter,
        setCurrentFilters,
        updateCurrentFilter,
        removeCurrentFilterById,
        onSearch: handleOnSearch,
        onDownload: handleOnDownload,
        allColumns: allColumns,
        currentColumns,
        loadingSearch,
        loadingDownload,
        schema,
        setCurrentColumns,
        addCurrentColumn,
        removeCurrentColumn,
        updateCurrentColumns,
        viewMode,
        setViewMode,
        getMetadataIdsPage: handleGetMetadataIdsPage,
        getCurrentMetadataIds: handleGetCurrentMetadataIds,
        dataResponse,
        tableApiRef,
        tableInitialState,
        onTableSaveState,
        onTablePaginationModelChange,
        onTableSortModelChange,
      }),
      [
        loading,
        availableFilters,
        currentFilters,
        addCurrentFilter,
        setCurrentFilters,
        updateCurrentFilter,
        removeCurrentFilterById,
        handleOnSearch,
        handleOnDownload,
        allColumns,
        currentColumns,
        loadingSearch,
        loadingDownload,
        schema,
        setCurrentColumns,
        addCurrentColumn,
        removeCurrentColumn,
        updateCurrentColumns,
        viewMode,
        setViewMode,
        handleGetMetadataIdsPage,
        handleGetCurrentMetadataIds,
        dataResponse,
        tableApiRef,
        tableInitialState,
        onTableSaveState,
        onTablePaginationModelChange,
        onTableSortModelChange,
      ],
    );
  };

export const useAdvancedSearchContext = (): AdvancedSearchContextType => {
  return assert(
    useContext(AdvancedSearchContext),
    'AdvancedSearchContext: context expected',
  );
};
